import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAppColor, getUserData, getIsExportingGrid, getIsAdmin } from "@icarius-app/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import { getUserDates, getIsLoading as getIsLoadingAnnotations } from "@icarius-pages/annotationsPage/selectors";
import AnnotationsPage from "@icarius-pages/annotationsPage/components/index";
import CommonPage from "@icarius-common/commonPage";
import { getIsLoading, getPhoto, getQueries, getKpiData, getEditableFields } from "../../selectors";
import {
    likeEmployeeAction,
    updateEmployeeInServer,
    createEmployeeInServer,
    getAvailableReceiptsForEmployee,
    uploadProfileImageAction,
    getQueriesGivenLevelAction,
    downloadPositionDescriptionAction,
} from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getRegularCertificatesAndCollaboratorsAction } from "@icarius-pages/certificates/actions";
import { getIsLoading as getIsLoadingCertif } from "@icarius-pages/certificates/selectors";
import ChangeProfileImageDialog from "@icarius-pages/myPersonalData/components/changeProfileImageDialog";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import { Folder, VisibilityIcon, FamilyGroupIcon, IndividualPayrollIcon } from "@icarius-icons";
import ResultGridDialog from "@icarius-common/automaticProcesses/components/resultGridDialog";
import PickTemplateDialog from "@icarius-pages/employeesProcesses/components/dialogs/pickTemplateDialog";
import { MyPeopleSchedule } from "@icarius-pages/myPeopleSchedule";
import { FamilyGroup } from "@icarius-pages/familyGroup";
import { HistoricalPeriods } from "@icarius-pages/historicalPeriods";
import { DistributionCost } from "@icarius-pages/distributionCost";
import { HistoricalDistributionCost } from "@icarius-pages/historicalDistributionCost";
import StyledHelpMenu from "@icarius-common/styledMenu";
import MenuItemWithIcon from "@icarius-common/MenuItemWithIcon";
import ListIcon from "@material-ui/icons/List";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { AnnexesPage } from "@icarius-pages/annexesPage";
import { getEmployeeVacationsAction } from "@icarius-pages/vacations/actions";
import { getIsLoading as getIsLoadingVacations } from "@icarius-pages/vacations/selectors";
import Loans from "@icarius-pages/loans/components";
import { getIssuePermissionsBalanceAction } from "@icarius-pages/issuePermissions/actions";
import { getBalanceData, getLoading as getIsLoadingPermissionAccount } from "@icarius-pages/issuePermissions/selectors";
import { IssueSummary } from "@icarius-pages/issueSummary";
import { AssetsDiscountsSummary } from "@icarius-pages/assetsDiscountsSummary";
import SelectProcessDialog from "@icarius-common/automaticProcesses/components/selectProcessDialog";
import { RESET_STATE } from "@icarius-pages/myPeopleSchedule/actionTypes";
import { MedalTable } from "@icarius-common/medalTable";
import { getAnnotationsAction } from "@icarius-pages/annotationsPage/actions";
import useAutomaticProcesses from "@icarius-common/automaticProcesses/useAutomaticProcesses";
import IndividualPayrollDialog from "../dialog/individualPayrollDialog";
import VacationsAccountDialog from "../dialog/vacationsAccountDialog";
import PermissionsAccountDialog from "../dialog/permissionsAccountDialog/permissionsAccountDialog";
import QueriesDialog from "../dialog/queriesDialog";
import UserSummary from "./userSummary";
import UserForm from "./userForm";
import CertificateDialog from "./certificateDialog";
import ReceiptDialog from "./receiptDialog";
import paths from "@icarius-router/paths";
import ViewDialog from "@icarius-pages/typesOfSkillsSpecific/components/dialogs/viewDialog";
import { getTypesOfSkillsIndicatorsAction } from "@icarius-pages/typesOfSkillsIndicators/actions";
import { CLEAR_STATE as CLEAR_STATE_INDICATORS } from "@icarius-pages/typesOfSkillsIndicators/actionTypes";
import useIsPathAvailable from "@icarius-utils/hooks/useIsPathAvailable";

const UserPage = (props) => {
    const { employee, isPeopleMaster, isDuplicate, handleCloseUserPage } = props;

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);
    const isLoadingCertif = useSelector(getIsLoadingCertif);
    const isLoadingAnnotations = useSelector(getIsLoadingAnnotations);
    const isLoadingPermissions = useSelector(getIsLoadingPermissionAccount);
    const isLoadingVacations = useSelector(getIsLoadingVacations);
    const employeeCurrentAccountPermissionsButton = useIsPathAvailable(paths.employeeCurrentAccountPermissionsButton);

    const color = useSelector(getAppColor);
    const userData = useSelector(getUserData);
    const photo = useSelector(getPhoto);
    const userDates = useSelector(getUserDates);
    const isExportinGrid = useSelector(getIsExportingGrid);
    const permissionAccountData = useSelector(getBalanceData);
    const queries = useSelector(getQueries);
    const kpiData = useSelector(getKpiData);
    const editableFields = useSelector(getEditableFields);
    const isAdmin = useSelector(getIsAdmin);

    const [changePhotoIsOpen, setChangePhotoIsOpen] = useState(false);
    const [tempPhoto, setTempPhoto] = useState(null);

    const [showMyPeopleSchedule, setShowMyPeopleSchedule] = useState(false);
    const [showFamilyGroup, setShowFamilyGroup] = useState(false);
    const [payrollDialogIsOpen, setPayrollDialogIsOpen] = useState(false);
    const [historicProcessesIsOpen, setHistoricProcessesIsOpen] = useState(false);
    const [summaryMenuAnchor, setSummaryMenuAnchor] = useState(null);
    const [issueSummaryIsOpen, setIssueSummaryIsOpen] = useState(false);
    const [assetsDiscountsSummaryIsOpen, setAssetsDiscountsSummaryIsOpen] = useState(false);
    const [annotationsIsOpen, setAnnotationsIsOpen] = useState(false);
    const [digitalFolderIsOpen, setDigitalFolderIsOpen] = useState(false);
    const [massiveMenuAnchor, setMassiveMenuAnchor] = useState(null);
    const [payrollMenuAnchor, setPayrollMenuAnchor] = useState(null);
    const [vacationsAccountDialogIsOpen, setVacationsAccountDialogIsOpen] = useState(false);
    const [loansAccountDialogIsOpen, setLoansAccountDialogIsOpen] = useState(false);
    const [permissionsAccountDialogIsOpen, setPermissionsAccountDialogIsOpen] = useState(false);
    const [distributionCostIsOpen, setDistributionCostIsOpen] = useState(false);
    const [historicalDistributionCostIsOpen, setHistoricalDistributionCostIsOpen] = useState(false);
    const [queriesDialogIsOpen, setQueriesDialogIsOpen] = useState(false);
    const [medalTableDialogIsOpen, setMedalTableDialogIsOpen] = useState(false);
    const [certificateDialogIsOpen, setCertificateDialogIsOpen] = useState(false);
    const [receiptDialogIsOpen, setReceiptDialogIsOpen] = useState(false);
    const [viewDialogIsOpen, setViewDialogIsOpen] = useState(false);

    const isNewEmployee = !employee;
    const isSameUser = employee?.["CODIGO DE EMPLEADO"] === userData?.code;

    const {
        isExecution,
        automaticProcessesIsLoading,
        automaticProcessesIsEnabled,
        processList,
        resultData,
        resultDialogIsOpen,
        pickTemplateDialogIsOpen,
        processSelectionDialogIsOpen,
        executeProcess,
        handleExecute,
        viewResult,
        handleViewResult,
        handleCloseProcessDialogSelection,
        handleCloseResultDialog,
        handleOpenPickTemplateDialog,
        handleClosePickTemplateDialog,
        handleGenerateDocumentsGivenTemplate,
    } = useAutomaticProcesses(paths.calculateProcessMyPeopleButton, !isPeopleMaster);

    const handleOpenVacationsAccount = () => {
        setMassiveMenuAnchor(null);
        dispatch(getEmployeeVacationsAction(userData.level, employee["CODIGO DE EMPLEADO"])).then((res) => {
            if (res?.status === 200) {
                setVacationsAccountDialogIsOpen(true);
            }
        });
    };

    const handleOpenLoansAccount = () => {
        setLoansAccountDialogIsOpen(true);
        setMassiveMenuAnchor(null);
    };

    const handleOpenPermissionAccount = () => {
        setMassiveMenuAnchor(null);
        dispatch(getIssuePermissionsBalanceAction(employee["CODIGO DE EMPLEADO"])).then((resp) => {
            if (resp?.status === 200) {
                setPermissionsAccountDialogIsOpen(true);
            }
        });
    };

    const handleSetTempEmployeePhoto = (data) => {
        setTempPhoto(data);
        dispatch(openSnackbarAction({ msg: getLocalizedString("creationUserPhotoInfo"), severity: "info" }));
    };

    const handleOpenChangePhotoDialog = () => {
        setChangePhotoIsOpen(true);
    };

    const handleCloseChangePhotoDialog = () => {
        setChangePhotoIsOpen(false);
    };

    const handleOpenCertificate = () => {
        dispatch(getRegularCertificatesAndCollaboratorsAction()).then((resp) => {
            if (resp?.status === 200) {
                if (
                    Boolean(
                        resp?.data?.colaborators?.find(
                            (emp) => emp["CODIGO DE EMPLEADO"] === employee["CODIGO DE EMPLEADO"]
                        )
                    ) &&
                    Boolean(resp?.data?.certificates?.length)
                ) {
                    setCertificateDialogIsOpen(true);
                } else {
                    dispatch(
                        openSnackbarAction({ msg: getLocalizedString("certificatesNotAvailable"), severity: "error" })
                    );
                }
            }
        });
    };

    const handleOpenReceipt = () => {
        dispatch(getAvailableReceiptsForEmployee(employee["CODIGO DE EMPLEADO"])).then((resp) => {
            if (resp?.status === 200) {
                if (!resp?.data?.receipts?.length) {
                    dispatch(
                        openSnackbarAction({ msg: getLocalizedString("receiptsNotAvailable"), severity: "error" })
                    );
                    return;
                }

                setReceiptDialogIsOpen(true);
            }
        });
    };

    const handleOpenAnnotations = () => {
        dispatch(getAnnotationsAction(employee["CODIGO DE EMPLEADO"])).then((resp) => {
            if (resp?.status === 200) {
                setAnnotationsIsOpen(true);
            }
        });
    };

    const handleOpenCompetencies = () => {
        dispatch(getTypesOfSkillsIndicatorsAction(null, employee["CODIGO DE CARGO"])).then((resp) => {
            if (resp?.status === 200) {
                setViewDialogIsOpen(true);
            }
        });
    };

    const handleCloseViewDialog = () => {
        setViewDialogIsOpen(false);
        dispatch({ type: CLEAR_STATE_INDICATORS });
    };

    const handleOpenQueries = () => {
        dispatch(
            getQueriesGivenLevelAction(
                employee["CODIGO DE EMPLEADO"],
                (isPeopleMaster ? paths.peopleMaster : paths.myPeople).replace("/", "")
            )
        ).then((resp) => {
            if (resp?.status === 200) {
                if (!resp?.data?.data?.length && !resp?.data?.kpiData?.length) {
                    dispatch(
                        openSnackbarAction({ msg: getLocalizedString("noKpiQueriesAvailable"), severity: "error" })
                    );
                    return;
                }

                setQueriesDialogIsOpen(true);
            }
        });
    };

    const handleUpdateEmployee = (employee) => {
        dispatch(
            updateEmployeeInServer({
                ...employee,
                old_codemp: employee["CODIGO DE EMPLEADO"],
            })
        );
    };

    const handleDownloadPositionDescription = () => {
        dispatch(downloadPositionDescriptionAction(employee["CODIGO DE EMPLEADO"]));
    };

    const handleCreateEmployee = (employee) => {
        let dataToSend = { ...employee };

        if (isDuplicate) {
            dataToSend = {
                ...dataToSend,
                origin_codemp: employee["CODIGO DE EMPLEADO"],
            };
        }

        dispatch(createEmployeeInServer(dataToSend)).then((resp) => {
            if (resp?.status === 200) {
                if (tempPhoto) {
                    dispatch(uploadProfileImageAction(tempPhoto, employee["CODIGO DE EMPLEADO"])).then((resp) => {
                        if (resp?.status === 200) {
                            handleCloseUserPage();
                        }
                    });
                } else {
                    handleCloseUserPage();
                }
            }
        });
    };

    const handleLikeRequest = (id, like, oldValue) => {
        if (!isSameUser) {
            dispatch(likeEmployeeAction(id, like, oldValue));
        }
    };

    const closeMyPeopleSchedule = () => {
        setShowMyPeopleSchedule(false);
        dispatch({ type: RESET_STATE });
    };

    const executeItem = () =>
        automaticProcessesIsEnabled &&
        !isNewEmployee &&
        !isSameUser &&
        isPeopleMaster && (
            <CustomIconButton
                title={
                    processList.length === 1
                        ? `${getLocalizedString("calculateProcess")}: ${processList[0].value}`
                        : getLocalizedString("calculateProcess")
                }
                onClick={() => handleExecute([employee["CODIGO DE EMPLEADO"]])}
                type={"execute"}
            />
        );

    const viewResultItem = () =>
        automaticProcessesIsEnabled &&
        !isNewEmployee &&
        !isSameUser &&
        isPeopleMaster && (
            <CustomIconButton
                title={getLocalizedString("viewResult")}
                onClick={() => handleViewResult(employee, employee["CODIGO DE EMPLEADO"])}
            >
                <VisibilityIcon />
            </CustomIconButton>
        );

    const goBackItem = () => (
        <CustomIconButton title={getLocalizedString("goBack")} onClick={handleCloseUserPage} type={"goBack"} />
    );

    const digitalFolderItem = () =>
        !isNewEmployee &&
        !isSameUser && (
            <CustomIconButton title={getLocalizedString("annex")} onClick={() => setDigitalFolderIsOpen(true)}>
                <Folder />
            </CustomIconButton>
        );

    const annotationsItem = () =>
        !isNewEmployee &&
        !isSameUser && (
            <CustomIconButton
                title={getLocalizedString("annotations")}
                onClick={handleOpenAnnotations}
                type={"annotations"}
            />
        );

    const positionCompetenciesItem = () =>
        !isNewEmployee &&
        !isSameUser &&
        employee?.positionHasSkills && (
            <CustomIconButton
                title={getLocalizedString("viewCompetenciesForThisPosition")}
                onClick={handleOpenCompetencies}
                type={"checklist"}
            />
        );

    const myPeopleScheduleItem = () =>
        !isNewEmployee &&
        !isSameUser && (
            <CustomIconButton
                title={getLocalizedString("viewCalendar")}
                onClick={() => setShowMyPeopleSchedule(true)}
                type={"calendar"}
            />
        );

    const familyGroupItem = () =>
        isPeopleMaster &&
        !isNewEmployee && (
            <CustomIconButton title={getLocalizedString("familiarGroup")} onClick={() => setShowFamilyGroup(true)}>
                <FamilyGroupIcon />
            </CustomIconButton>
        );

    const medalTableItem = () =>
        !isNewEmployee && (
            <CustomIconButton
                title={getLocalizedString("medalTable")}
                onClick={() => setMedalTableDialogIsOpen(true)}
                type="medalTable"
            />
        );

    const payrollItem = () =>
        isPeopleMaster &&
        !isNewEmployee && (
            <>
                <CustomIconButton
                    title={getLocalizedString("payrollsAndHistories")}
                    onClick={(e) => setPayrollMenuAnchor(e.currentTarget)}
                >
                    <IndividualPayrollIcon />
                </CustomIconButton>
                <StyledHelpMenu
                    anchorEl={payrollMenuAnchor}
                    isOpen={Boolean(payrollMenuAnchor)}
                    onClose={() => setPayrollMenuAnchor(null)}
                >
                    {!isNewEmployee && (!isSameUser || (isSameUser && isAdmin)) && isPeopleMaster && (
                        <MenuItemWithIcon
                            text={getLocalizedString("costDistribution")}
                            onClick={() => {
                                setDistributionCostIsOpen(true);
                                setPayrollMenuAnchor(null);
                            }}
                        />
                    )}
                    {!isNewEmployee && (!isSameUser || (isSameUser && isAdmin)) && isPeopleMaster && (
                        <MenuItemWithIcon
                            text={getLocalizedString("historicalCostDistribution")}
                            onClick={() => {
                                setHistoricalDistributionCostIsOpen(true);
                                setPayrollMenuAnchor(null);
                            }}
                        />
                    )}
                    <MenuItemWithIcon
                        text={getLocalizedString("payrollEnablement")}
                        onClick={() => {
                            setPayrollDialogIsOpen(true);
                            setPayrollMenuAnchor(null);
                        }}
                    />
                    <MenuItemWithIcon
                        text={getLocalizedString("historicalProcessesTitle")}
                        onClick={() => {
                            setHistoricProcessesIsOpen(true);
                            setPayrollMenuAnchor(null);
                        }}
                    />
                </StyledHelpMenu>
            </>
        );

    const summaryItem = () =>
        !isNewEmployee &&
        !isSameUser &&
        isPeopleMaster && (
            <>
                <CustomIconButton
                    title={getLocalizedString("assetsDiscountsIssues")}
                    onClick={(e) => setSummaryMenuAnchor(e.currentTarget)}
                >
                    <ListAltIcon />
                </CustomIconButton>
                <StyledHelpMenu
                    anchorEl={summaryMenuAnchor}
                    isOpen={Boolean(summaryMenuAnchor)}
                    onClose={() => setSummaryMenuAnchor(null)}
                >
                    <MenuItemWithIcon
                        text={getLocalizedString("issueSummary")}
                        onClick={() => {
                            setSummaryMenuAnchor(null);
                            setIssueSummaryIsOpen(true);
                        }}
                    />
                    <MenuItemWithIcon
                        text={getLocalizedString("assetsDiscountsSummary")}
                        onClick={() => {
                            setSummaryMenuAnchor(null);
                            setAssetsDiscountsSummaryIsOpen(true);
                        }}
                    />
                </StyledHelpMenu>
            </>
        );

    const accountsItem = () =>
        !isNewEmployee &&
        !isSameUser && (
            <>
                <CustomIconButton
                    title={getLocalizedString("currentAccounts")}
                    onClick={(e) => setMassiveMenuAnchor(e.currentTarget)}
                >
                    <ListIcon />
                </CustomIconButton>
                <StyledHelpMenu
                    anchorEl={massiveMenuAnchor}
                    isOpen={Boolean(massiveMenuAnchor)}
                    onClose={() => setMassiveMenuAnchor(null)}
                >
                    <MenuItemWithIcon
                        text={getLocalizedString("vacationRequestTitle")}
                        onClick={handleOpenVacationsAccount}
                    />
                    {isPeopleMaster && (
                        <MenuItemWithIcon text={getLocalizedString("loans")} onClick={handleOpenLoansAccount} />
                    )}
                    {employeeCurrentAccountPermissionsButton && (
                        <MenuItemWithIcon
                            text={getLocalizedString("permissions")}
                            onClick={handleOpenPermissionAccount}
                        />
                    )}
                </StyledHelpMenu>
            </>
        );

    const customItems = isDuplicate
        ? [goBackItem]
        : [
              goBackItem,
              executeItem,
              viewResultItem,
              myPeopleScheduleItem,
              digitalFolderItem,
              annotationsItem,
              positionCompetenciesItem,
              familyGroupItem,
              payrollItem,
              accountsItem,
              summaryItem,
              medalTableItem,
          ];

    const hideCommonPage =
        showMyPeopleSchedule ||
        showFamilyGroup ||
        historicProcessesIsOpen ||
        distributionCostIsOpen ||
        historicalDistributionCostIsOpen ||
        loansAccountDialogIsOpen ||
        digitalFolderIsOpen ||
        issueSummaryIsOpen ||
        assetsDiscountsSummaryIsOpen ||
        annotationsIsOpen;

    const isLoadingAnything =
        isLoading ||
        isLoadingCertif ||
        isLoadingAnnotations ||
        isLoadingPermissions ||
        isLoadingVacations ||
        automaticProcessesIsLoading;

    return (
        <>
            {certificateDialogIsOpen && (
                <CertificateDialog
                    open={certificateDialogIsOpen}
                    code={employee["CODIGO DE EMPLEADO"]}
                    handleClose={() => setCertificateDialogIsOpen(false)}
                />
            )}
            {receiptDialogIsOpen && (
                <ReceiptDialog
                    open={receiptDialogIsOpen}
                    code={employee["CODIGO DE EMPLEADO"]}
                    handleClose={() => setReceiptDialogIsOpen(false)}
                />
            )}
            {showMyPeopleSchedule && (
                <MyPeopleSchedule
                    presetCode={employee["CODIGO DE EMPLEADO"]}
                    presetHandleGoBack={closeMyPeopleSchedule}
                />
            )}
            {showFamilyGroup && (
                <FamilyGroup
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setShowFamilyGroup(false)}
                />
            )}
            {payrollDialogIsOpen && (
                <IndividualPayrollDialog
                    open={payrollDialogIsOpen}
                    loading={isLoadingAnything}
                    code={employee["CODIGO DE EMPLEADO"]}
                    handleClose={() => setPayrollDialogIsOpen(false)}
                />
            )}
            {historicProcessesIsOpen && (
                <HistoricalPeriods
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setHistoricProcessesIsOpen(false)}
                />
            )}
            {issueSummaryIsOpen && (
                <IssueSummary
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setIssueSummaryIsOpen(false)}
                />
            )}
            {assetsDiscountsSummaryIsOpen && (
                <AssetsDiscountsSummary
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setAssetsDiscountsSummaryIsOpen(false)}
                />
            )}
            {distributionCostIsOpen && (
                <DistributionCost
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setDistributionCostIsOpen(false)}
                />
            )}
            {historicalDistributionCostIsOpen && (
                <HistoricalDistributionCost
                    code={employee["CODIGO DE EMPLEADO"]}
                    name={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setHistoricalDistributionCostIsOpen(false)}
                />
            )}
            {annotationsIsOpen && (
                <AnnotationsPage
                    employee={employee}
                    userDates={userDates}
                    handleClose={() => setAnnotationsIsOpen(false)}
                />
            )}
            {medalTableDialogIsOpen && (
                <MedalTable
                    userCode={employee["CODIGO DE EMPLEADO"]}
                    handleClose={() => setMedalTableDialogIsOpen(false)}
                    dialog
                />
            )}
            {vacationsAccountDialogIsOpen && !isLoadingVacations && (
                <VacationsAccountDialog
                    open={vacationsAccountDialogIsOpen}
                    employeeCode={employee["CODIGO DE EMPLEADO"]}
                    employeeName={employee["APELLIDO Y NOMBRE"]}
                    handleClose={() => setVacationsAccountDialogIsOpen(false)}
                />
            )}
            {loansAccountDialogIsOpen && (
                <Loans
                    employeeCode={employee["CODIGO DE EMPLEADO"]}
                    employeeName={employee["APELLIDO Y NOMBRE"]}
                    handleGoBack={() => setLoansAccountDialogIsOpen(false)}
                />
            )}
            {permissionsAccountDialogIsOpen && !isLoadingPermissions && (
                <PermissionsAccountDialog
                    open={permissionsAccountDialogIsOpen}
                    color={color}
                    data={permissionAccountData}
                    employeeCode={employee["CODIGO DE EMPLEADO"]}
                    employeeName={employee["APELLIDO Y NOMBRE"]}
                    loading={isLoadingPermissions}
                    handleClose={() => setPermissionsAccountDialogIsOpen(false)}
                    isCRUD
                />
            )}
            {digitalFolderIsOpen && (
                <AnnexesPage
                    employeeCode={employee["CODIGO DE EMPLEADO"]}
                    employeeName={employee["APELLIDO Y NOMBRE"]}
                    handleClose={() => setDigitalFolderIsOpen(false)}
                />
            )}
            {queriesDialogIsOpen && (
                <QueriesDialog
                    open={queriesDialogIsOpen}
                    queries={queries}
                    employeeCode={employee["CODIGO DE EMPLEADO"]}
                    kpiData={kpiData}
                    handleClose={() => setQueriesDialogIsOpen(false)}
                />
            )}
            {viewDialogIsOpen && (
                <ViewDialog
                    open={viewDialogIsOpen}
                    data={{ group: employee["CARGO"] || employee["Nombre del cargo"] }}
                    handleClose={handleCloseViewDialog}
                    isPositionView={true}
                />
            )}
            <div style={{ display: hideCommonPage ? "none" : "block" }}>
                <CommonPage
                    title={
                        isPeopleMaster ? getLocalizedString("employeeMaster") : getLocalizedString("myPeopleTableTitle")
                    }
                    menuItems={customItems}
                    isLoading={isLoadingAnything}
                    isNotGridPage
                >
                    <div id={"main-content"} style={{ padding: 50, paddingTop: 30 }}>
                        <UserSummary
                            isNewEmployee={isNewEmployee || isDuplicate}
                            isSameUser={isSameUser}
                            isPhotoModificable={isPeopleMaster}
                            employee={employee}
                            photo={photo || employee?.["FOTOGRAFIA"]} // photo es la nueva (ya guardada en el sv) pero que todavia no se setea en employee que es un ojeto viejo
                            editableFields={editableFields}
                            handleLikeRequest={handleLikeRequest}
                            handleOpenCertificate={handleOpenCertificate}
                            handleOpenReceipt={handleOpenReceipt}
                            handleOpenChangePhotoDialog={handleOpenChangePhotoDialog}
                            handleOpenQueries={handleOpenQueries}
                            handleDownloadPositionDescription={handleDownloadPositionDescription}
                        />
                        <UserForm
                            data={employee}
                            isDuplicate={isDuplicate}
                            handleCreateEmployee={handleCreateEmployee}
                            handleUpdateEmployee={handleUpdateEmployee}
                        />
                    </div>
                    {changePhotoIsOpen && (
                        <ChangeProfileImageDialog
                            open={changePhotoIsOpen}
                            isPeopleMaster={isPeopleMaster}
                            isNewEmployee={isNewEmployee}
                            handleSubmit={handleSetTempEmployeePhoto}
                            profileImage={isDuplicate ? null : photo || employee?.["FOTOGRAFIA"] || null}
                            userCode={employee?.["CODIGO DE EMPLEADO"]}
                            handleClose={handleCloseChangePhotoDialog}
                        />
                    )}
                    {processSelectionDialogIsOpen && (
                        <SelectProcessDialog
                            open={processSelectionDialogIsOpen}
                            title={
                                isExecution
                                    ? getLocalizedString("selectProcessToRun")
                                    : getLocalizedString("selectProcessToQuery")
                            }
                            processList={processList}
                            handleClose={handleCloseProcessDialogSelection}
                            handleExecute={isExecution ? executeProcess : viewResult}
                        />
                    )}
                    {resultDialogIsOpen && !isLoadingAnything && !isExportinGrid && (
                        <ResultGridDialog
                            open={resultDialogIsOpen}
                            templates={resultData.templates}
                            process={resultData.process}
                            employeeName={resultData.employee["APELLIDO Y NOMBRE"]}
                            handleClose={handleCloseResultDialog}
                            handleOpenPickTemplateDialog={handleOpenPickTemplateDialog}
                            handleGenerateDocumentsGivenTemplate={handleGenerateDocumentsGivenTemplate}
                        />
                    )}
                    {resultData?.templates?.length > 0 &&
                        pickTemplateDialogIsOpen &&
                        !isLoadingAnything &&
                        !isExportinGrid && (
                            <PickTemplateDialog
                                open={pickTemplateDialogIsOpen}
                                templates={resultData.templates}
                                handleConfirm={handleGenerateDocumentsGivenTemplate}
                                handleClose={handleClosePickTemplateDialog}
                            />
                        )}
                </CommonPage>
            </div>
        </>
    );
};

export default UserPage;
